import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Box, IconButton, Tooltip, Typography } from '@mui/material';
import FullCalendar from '@fullcalendar/react';
import dayGridPlugin from '@fullcalendar/daygrid';
import timeGridPlugin from '@fullcalendar/timegrid';
import interactionPlugin from '@fullcalendar/interaction';
import huLocale from '@fullcalendar/core/locales/hu';
import dayjs from 'dayjs';
import CalendarFilter from './CalendarFilter';
import useUsers from '../../../../context/UsersContext';
import masterDataService from '../../../../service/masterData.service';
import calendarService from '../../../../service/client/delVill/calendar.service';
import colorFinder from '../../../../utils/colorFinder';
import { sxConfig } from './calendarStyle';
import simpletaskService from '../../../../service/simpletask.service';
import SimpleTaskDialog from './SimpleTaskDialog';
import TooltipContent from './TooltipContent';
import useConfig from '../../../../context/ConfigContext';
import { DialogUI } from '../../../Interface';
import { TaskReportForm } from '../../../TaskReport/TaskReportForm';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import useParam from '../../../../context/ParamContext';
import { generateDefaultTime } from '../../../../utils/helper';

export const DVCalendar = () => {
  const { user, getUser } = useUsers();
  const { getParam } = useParam();
  const { config, getConfig } = useConfig();
  const calendarRef = useRef(null);
  const [events, setEvents] = useState([]);
  const [dateRange, setDateRange] = useState({});
  const [query, setQuery] = useState({
    filter: { filterModel: { items: [], logicOperator: 'and' } },
  });
  const [resources, setResources] = useState({
    resourceTwo: [],
    resourceFour: [],
  });
  const [date, setDate] = useState({
    startDate: null,
    endDate: null,
  });
  const [taskDialogOpen, setTaskDialogOpen] = useState(false);
  const [taskChanged, setTaskChanged] = useState(false);
  const [headerOffset, setHeaderOffset] = useState(0);
  const [showReportDialog, setShowReportDialog] = useState(false);
  const [task, setTask] = useState({});

  const getFilterItems = () => query?.filter?.filterModel?.items ?? [];

  const hasResourceFilter = useMemo(() => {
    const items = getFilterItems();

    return items.some((item) => {
      const isResourceField = item.field === 'resourceTwoId' || item.field === 'resourceFourId';

      const values = Array.isArray(item.value) ? item.value : [];
      return isResourceField && values.length > 0;
    });
  }, [query]);

  useEffect(() => {
    if (!Object.keys(user).length) getUser();
    initDefaultValues();
    getConfig();
    getParam();
  }, []);

  useEffect(() => {
    const mq = window.matchMedia('(min-width: 1024px)');
    const update = () => setHeaderOffset(mq.matches ? 64 : 0);
    update();
    if (mq.addEventListener) {
      mq.addEventListener('change', update);
      return () => mq.removeEventListener('change', update);
    } else {
      mq.addListener(update);
      return () => mq.removeListener(update);
    }
  }, []);

  useEffect(() => {
    if (date.startDate !== null && date.endDate !== null && calendarRef.current) {
      const calendarApi = calendarRef.current.getApi();

      if (calendarApi.view.type !== 'dayGridDay') {
        calendarApi.changeView('dayGridDay', date.startDate.toDate());
      } else {
        calendarApi.gotoDate(date.startDate.toDate());
      }
    }
  }, [date.startDate, date.endDate]);

  useEffect(() => {
    if (date.startDate !== null && date.endDate !== null) {
      setDateRange(date);
    }
  }, [date.startDate, date.endDate]);

  useEffect(() => {
    if (dateRange.startDate && dateRange.endDate && hasResourceFilter) {
      getAllCalendarDays();
    } else {
      setEvents([]);
    }
  }, [dateRange.startDate, dateRange.endDate, hasResourceFilter, query]);

  useEffect(() => {
    if (taskChanged && hasResourceFilter) getAllCalendarDays();
  }, [taskChanged]);

  const initDefaultValues = () => {
    const promiseArray = [];

    promiseArray.push(
      new Promise((resolve, reject) => {
        masterDataService
          .resourceTwoToForm()
          .then((data) => {
            resolve(data);
          })
          .catch((e) => {
            reject(e);
          });
      })
    );

    promiseArray.push(
      new Promise((resolve, reject) => {
        masterDataService
          .resourceFourToForm()
          .then((data) => {
            resolve(data);
          })
          .catch((e) => {
            reject(e);
          });
      })
    );

    Promise.all(promiseArray)
      .then((resp) => {
        const resources = {
          resourceTwo: resp[0],
          resourceFour: resp[1],
        };

        setResources(resources);
      })
      .finally(() => {
        //hideLoader();
      });
  };

  const calendarViews = {
    dayGridMonth: {
      dayHeaderFormat: { weekday: 'long' },
    },
    dayGridWeek: {
      dayHeaderFormat: { month: 'long', day: 'numeric', weekday: 'long' },
      slotLabelFormat: [{ hour: 'numeric', minute: '2-digit' }],
    },
    dayGridDay: {
      slotLabelFormat: [{ hour: 'numeric', minute: '2-digit' }],
    },
  };

  const getAllCalendarDays = () => {
    const reqData = {
      query,
      dateRange,
    };
    calendarService.getCalendarDays(reqData).then((data) => {
      const coloredData = data.map((event) => {
        const status = event?.status?.color;
        const color = colorFinder(status);
        return {
          ...event,
          backgroundColor: color.colorCode,
        };
      });
      setEvents(coloredData);
      setTaskChanged(false);
    });
  };

  const handleDatesSet = (dateInfo) => {
    const startDate = dayjs(dateInfo.start).startOf('day');
    const endDate = dayjs(dateInfo.end).endOf('day');

    if (dateRange.startDate?.isSame(startDate, 'day') && dateRange.endDate?.isSame(endDate, 'day')) {
      return;
    }
    setDateRange({ startDate, endDate });
  };

  const getTaskById = async (id) => simpletaskService.getTaskById(id);

  const handleSelectDate = (selectInfo) => {
    const { start, allDay } = selectInfo;

    let startDate = dayjs(start);

    if (allDay) {
      startDate = config.defTaskFromTime
        ? generateDefaultTime(startDate, config.defTaskFromTime)
        : startDate.startOf('day');
    }

    const duration = config?.defTaskDuration ?? 1;

    setTaskDialogOpen(true);
    setTask({
      startDate,
      endDate: dayjs(startDate).add(duration, 'hour'),
      duration,
    });
  };

  return (
    <div
      className="taskListCalendar calendarView flex bg-[#f3f4f6]"
      style={{ height: `calc(100dvh - ${headerOffset}px)` }}
    >
      <div
        className="overflow-y-scroll overflow-x-hidden custom-scrollbar w-[380px]"
        style={{ height: `calc(100dvh - ${headerOffset + 20}px)` }}
      >
        <CalendarFilter
          dates={{ date, setDate }}
          resources={resources}
          queryState={{ query, setQuery }}
          hasResourceFilter={hasResourceFilter}
        />
      </div>
      <div className="w-full bg-white">
        <FullCalendar
          ref={calendarRef}
          plugins={[dayGridPlugin, timeGridPlugin, interactionPlugin]}
          initialView="dayGridWeek"
          headerToolbar={{
            left: 'prev,next,today',
            center: 'title',
            right: 'dayGridDay,dayGridWeek,dayGridMonth',
          }}
          locales={[huLocale]}
          views={calendarViews}
          datesSet={handleDatesSet}
          nowIndicator={true}
          dayMaxEventRows={true}
          events={events}
          selectable={true}
          select={handleSelectDate}
          eventClick={async (eventInfo) => {
            const { event } = eventInfo;
            const data = await getTaskById(event.id);
            if (data) {
              setTaskDialogOpen(true);
              setTask(data);
            }
          }}
          eventContent={(eventInfo) => {
            return (
              <EventContent
                eventInfo={eventInfo}
                getTaskById={getTaskById}
                setTask={setTask}
                setShowReportDialog={setShowReportDialog}
              />
            );
          }}
        />
      </div>

      <SimpleTaskDialog
        open={taskDialogOpen}
        handleClose={() => {
          setTaskDialogOpen(false);
          setTask({});
        }}
        task={task}
        changed={{ taskChanged, setTaskChanged }}
      />

      <DialogUI
        open={showReportDialog}
        headerContent={'Feladat lejelentő'}
        fullWidth={true}
        onClose={() => setShowReportDialog(false)}
      >
        <TaskReportForm
          selectedTask={task}
          setShowDialog={setShowReportDialog}
          onReportAdd={() => {
            setShowReportDialog(false);
            setTaskChanged(true);
          }}
        />
      </DialogUI>
    </div>
  );
};

const EventContent = ({ eventInfo, getTaskById, setTask, setShowReportDialog }) => {
  const { params } = useParam();
  const extendedProps = eventInfo.event.extendedProps;
  const cardColor =
    params.DV_CALENDAR_CARD_USE_STATUS_COLOR === 'Y' ? extendedProps.status?.color : extendedProps.taskType?.color;
  const projectName = extendedProps?.project?.projectName;
  const title = eventInfo.event.title;

  const color = colorFinder(cardColor);
  const textColor = color.textColor || '#000';

  return (
    <Tooltip
      arrow
      placement="top"
      title={<TooltipContent eventInfo={eventInfo} />}
      slotProps={{
        tooltip: {
          sx: sxConfig.tooltip,
        },
        arrow: { sx: sxConfig.tooltipArrow },
      }}
    >
      <Box
        sx={{
          ...sxConfig.eventBox,
          backgroundColor: color.colorBgCode,
          borderLeft: `3px solid ${textColor}`,
        }}
      >
        <Typography variant="caption" component="span" sx={{ ...sxConfig.eventTitle, color: textColor }}>
          {projectName ?? `N/A`}
        </Typography>
        <Box>
          <Typography component="span" sx={{ ...sxConfig.eventTitle, color: textColor }}>
            {title}
          </Typography>
        </Box>
        <Box>
          <Typography component="span" sx={{ ...sxConfig.eventTitle, color: textColor }}>
            {extendedProps.generatedType === '' ? '\u00A0' : extendedProps.generatedType}
          </Typography>
        </Box>
        <Box>
          <Typography component="span" sx={{ ...sxConfig.eventTitle, color: textColor }}>
            {extendedProps.generatedOrderNumber}
          </Typography>
        </Box>
        {params.DV_CALENDAR_SHOW_REPORT_ICON === 'Y' && (
          <Box sx={sxConfig.reportIconContainer}>
            <IconButton
              sx={sxConfig.reportIconButton}
              onClick={async (e) => {
                e.stopPropagation();
                const data = await getTaskById(eventInfo.event.id);
                if (data) {
                  setTask(data);
                  setShowReportDialog(true);
                }
              }}
            >
              <AccessTimeIcon sx={sxConfig.reportIcon} />
            </IconButton>
          </Box>
        )}
      </Box>
    </Tooltip>
  );
};
